import { useState } from "react";
import { Link } from "react-router-dom";
import { useGameState } from "@/hooks/useGameState";
import GameBoard from "./components/GameBoard";
import SidePanel from "./components/SidePanel";
import IncidentCardModal from "./components/IncidentCardModal";
import { SnakeModal, LadderModal } from "./components/SnakeLadderModal";
import ResultsScreen from "./components/ResultsScreen";

const DICE_FACES = ["⚀", "⚁", "⚂", "⚃", "⚄", "⚅"];

export default function GamePage() {
  const { state, rollDice, resolveCard, resolveSnake, resolveLadder, restartGame, currentRank } = useGameState();
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);

  if (state.gameOver) {
    return (
      <ResultsScreen
        currentSquare={state.currentSquare}
        turn={state.turn}
        correctCards={state.correctCards}
        onPlayAgain={restartGame}
      />
    );
  }

  const canRoll = !state.isRolling && !state.pendingCard && !state.pendingSnake && !state.pendingLadder;

  return (
    <div
      className="h-screen bg-[#0a0a0a] text-white font-mono overflow-hidden flex flex-col"
      style={{ maxHeight: "100dvh" }}
    >
      {/* Subtle grid background */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,255,136,0.018) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,136,0.018) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* ── Top bar ── */}
      <header
        className="relative z-10 flex-shrink-0 h-11 flex items-center justify-between px-3 md:px-5"
        style={{ background: "#0a0a0a", borderBottom: "1px solid rgba(255,255,255,0.05)" }}
      >
        {/* Left */}
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2 cursor-pointer group">
            <img
              src="https://static.readdy.ai/image/7a396de9d4a51ca3342f9bdb460bfa21/efd74d9ca1c0839b36715e646624f840.png"
              alt="Incident Ladder"
              className="h-5 w-auto"
            />
            <span className="text-[11px] text-white/30 font-mono group-hover:text-white/50 transition-colors hidden sm:block">
              IncidentLadder
            </span>
          </Link>
          <span className="text-white/15 font-mono text-xs hidden sm:block">/</span>
          <span className="text-[11px] text-white/35 font-mono hidden sm:block">game_board</span>
        </div>

        {/* Center: live status pills */}
        <div className="flex items-center gap-1.5">
          <div
            className="flex items-center gap-1.5 px-2 py-0.5 rounded"
            style={{ background: "rgba(0,217,255,0.06)", border: "1px solid rgba(0,217,255,0.15)" }}
          >
            <i className="ri-map-pin-line text-[#00d9ff] text-[10px]"></i>
            <span className="text-[11px] font-mono text-white/60">
              sq <span className="text-[#00d9ff] font-bold">{state.currentSquare}</span>
              <span className="text-white/20">/100</span>
            </span>
          </div>
          <div
            className="items-center gap-1.5 px-2 py-0.5 rounded hidden sm:flex"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}
          >
            <i className="ri-refresh-line text-white/25 text-[10px]"></i>
            <span className="text-[11px] font-mono text-white/40">
              turn <span className="text-white/60 font-bold">{state.turn}</span>
            </span>
          </div>
          <div
            className="items-center gap-1.5 px-2 py-0.5 rounded hidden md:flex"
            style={{ background: `${currentRank.color}08`, border: `1px solid ${currentRank.color}20` }}
          >
            <i className={`${currentRank.icon} text-[10px]`} style={{ color: currentRank.color }}></i>
            <span className="text-[11px] font-mono font-bold" style={{ color: currentRank.color }}>
              {currentRank.rank}
            </span>
          </div>
        </div>

        {/* Right */}
        <div className="flex items-center gap-2">
          {/* Mobile side panel toggle */}
          <button
            onClick={() => setSidePanelOpen((v) => !v)}
            className="xl:hidden flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-mono transition-all cursor-pointer whitespace-nowrap"
            style={{ border: "1px solid rgba(0,217,255,0.2)", color: "rgba(0,217,255,0.5)" }}
          >
            <i className="ri-dashboard-line text-[10px]"></i>
            <span className="hidden sm:inline">Stats</span>
          </button>
          <button
            onClick={() => setShowRestartConfirm(true)}
            className="flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-mono transition-all cursor-pointer whitespace-nowrap"
            style={{ border: "1px solid rgba(255,68,68,0.2)", color: "rgba(255,68,68,0.5)" }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,68,68,0.4)";
              (e.currentTarget as HTMLElement).style.color = "#ff4444";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,68,68,0.2)";
              (e.currentTarget as HTMLElement).style.color = "rgba(255,68,68,0.5)";
            }}
          >
            <i className="ri-restart-line text-[10px]"></i>
            <span className="hidden sm:inline">Restart</span>
          </button>
        </div>
      </header>

      {/* ── Main content — fills remaining height ── */}
      <div className="relative z-10 flex-1 flex overflow-hidden min-h-0">

        {/* ── Board + controls column ── */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden p-2 md:p-3 gap-2">

          {/* Board panel — takes all available vertical space */}
          <div
            className="flex-1 min-h-0 rounded-xl p-2 md:p-3 flex flex-col"
            style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.06)" }}
          >
            {/* Board fills the panel, constrained by both width and height */}
            <div className="flex-1 min-h-0 flex items-center justify-center">
              <div
                className="w-full h-full"
                style={{
                  /* The board must fit within the available box — use the smaller dimension */
                  maxWidth: "min(100%, calc(100vh - 220px))",
                  maxHeight: "min(100%, calc(100vw - 340px))",
                  aspectRatio: "1 / 1",
                  margin: "0 auto",
                  containerType: "inline-size",
                }}
              >
                <GameBoard currentSquare={state.currentSquare} />
              </div>
            </div>
          </div>

          {/* ── Controls panel — compact, fixed height ── */}
          <div
            className="flex-shrink-0 rounded-xl px-3 py-2.5"
            style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.06)" }}
          >
            <div className="flex items-center gap-3">

              {/* Dice face */}
              <div
                className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-lg transition-all"
                style={{
                  border: state.isRolling
                    ? "1px solid rgba(0,217,255,0.5)"
                    : "1px solid rgba(255,255,255,0.08)",
                  background: state.isRolling
                    ? "rgba(0,217,255,0.06)"
                    : "rgba(255,255,255,0.02)",
                  boxShadow: state.isRolling ? "0 0 16px rgba(0,217,255,0.15)" : "none",
                }}
              >
                {state.isRolling ? (
                  <i className="ri-loader-4-line text-2xl text-[#00d9ff] animate-spin"></i>
                ) : state.diceValue ? (
                  <span className="text-3xl leading-none select-none">{DICE_FACES[state.diceValue - 1]}</span>
                ) : (
                  <i className="ri-dice-line text-2xl text-white/15"></i>
                )}
              </div>

              {/* Roll info */}
              <div className="flex-shrink-0 hidden sm:block">
                {state.diceValue && !state.isRolling ? (
                  <div>
                    <div className="text-[9px] text-white/20 font-mono">last_roll</div>
                    <div className="text-2xl font-black text-white leading-none">{state.diceValue}</div>
                    <div className="text-[9px] text-white/20 font-mono">→ sq {state.currentSquare}</div>
                  </div>
                ) : (
                  <div>
                    <div className="text-[9px] text-white/15 font-mono">awaiting</div>
                    <div className="text-xl font-black text-white/10 leading-none">—</div>
                  </div>
                )}
              </div>

              {/* Roll button */}
              <button
                onClick={rollDice}
                disabled={!canRoll}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-black text-sm transition-all whitespace-nowrap"
                style={{
                  background: canRoll ? "#00ff88" : "rgba(255,255,255,0.04)",
                  color: canRoll ? "#0a0a0a" : "rgba(255,255,255,0.15)",
                  cursor: canRoll ? "pointer" : "not-allowed",
                  border: canRoll ? "none" : "1px solid rgba(255,255,255,0.06)",
                  boxShadow: canRoll ? "0 0 20px rgba(0,255,136,0.2)" : "none",
                }}
              >
                {state.isRolling ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Rolling...
                  </>
                ) : (
                  <>
                    <i className="ri-dice-line"></i>
                    Roll Dice
                  </>
                )}
              </button>

              {/* Position */}
              <div className="flex-shrink-0 text-right">
                <div className="text-[9px] text-white/20 font-mono">position</div>
                <div className="text-2xl font-black leading-none" style={{ color: "#00d9ff" }}>
                  {state.currentSquare}
                </div>
                <div className="text-[9px] text-white/15 font-mono">/ 100</div>
              </div>
            </div>

            {/* Pending state indicator */}
            {(state.pendingCard || state.pendingSnake || state.pendingLadder) && (
              <div
                className="mt-2 flex items-center gap-2 px-3 py-1.5 rounded-lg"
                style={{ background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)" }}
              >
                <i className="ri-time-line text-[#f59e0b] text-xs flex-shrink-0"></i>
                <span className="text-[10px] text-[#f59e0b]/70 font-mono">
                  {state.pendingCard
                    ? "Incident card active — resolve before rolling"
                    : state.pendingSnake
                    ? "Failure pattern triggered — acknowledge to continue"
                    : "Reliability win — confirm to advance"}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Side panel — desktop always visible, mobile slide-in ── */}
        <>
          {/* Desktop side panel */}
          <div
            className="hidden xl:flex flex-col w-64 2xl:w-72 flex-shrink-0 overflow-y-auto overflow-x-hidden p-2 md:p-3 gap-2"
            style={{ borderLeft: "1px solid rgba(255,255,255,0.04)" }}
          >
            <SidePanel
              events={state.events}
              currentSquare={state.currentSquare}
              turn={state.turn}
              correctCards={state.correctCards}
              rankLabel={currentRank.rank}
              rankColor={currentRank.color}
              rankIcon={currentRank.icon}
            />
          </div>

          {/* Mobile/tablet slide-in overlay */}
          {sidePanelOpen && (
            <div className="xl:hidden fixed inset-0 z-40 flex">
              <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={() => setSidePanelOpen(false)}
              />
              <div
                className="relative z-10 ml-auto w-72 max-w-[85vw] h-full overflow-y-auto p-3 flex flex-col gap-2"
                style={{ background: "#0d0d0d", borderLeft: "1px solid rgba(255,255,255,0.08)" }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-white/30 font-mono uppercase tracking-widest">SRE Dashboard</span>
                  <button
                    onClick={() => setSidePanelOpen(false)}
                    className="w-6 h-6 flex items-center justify-center rounded text-white/30 hover:text-white/60 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
                <SidePanel
                  events={state.events}
                  currentSquare={state.currentSquare}
                  turn={state.turn}
                  correctCards={state.correctCards}
                  rankLabel={currentRank.rank}
                  rankColor={currentRank.color}
                  rankIcon={currentRank.icon}
                />
              </div>
            </div>
          )}
        </>
      </div>

      {/* ── Modals ── */}
      {state.pendingCard && (
        <IncidentCardModal card={state.pendingCard} onResolve={resolveCard} />
      )}
      {state.pendingSnake && (
        <SnakeModal snake={state.pendingSnake} onContinue={resolveSnake} />
      )}
      {state.pendingLadder && (
        <LadderModal ladder={state.pendingLadder} onContinue={resolveLadder} />
      )}

      {/* ── Restart confirm ── */}
      {showRestartConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/85 backdrop-blur-sm"
            onClick={() => setShowRestartConfirm(false)}
          />
          <div
            className="relative z-10 w-full max-w-sm rounded-xl p-6"
            style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                style={{ background: "rgba(255,68,68,0.1)", border: "1px solid rgba(255,68,68,0.25)" }}
              >
                <i className="ri-restart-line text-[#ff4444] text-sm"></i>
              </div>
              <div>
                <div className="text-[10px] text-white/25 font-mono uppercase tracking-widest">Confirm Action</div>
                <h3 className="text-white font-bold text-base">Restart Session?</h3>
              </div>
            </div>
            <p className="text-white/35 text-sm font-mono mb-5 leading-relaxed">
              All progress will be lost. Turn {state.turn}, square {state.currentSquare}, {state.correctCards} correct cards.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { restartGame(); setShowRestartConfirm(false); }}
                className="flex-1 py-2.5 rounded-lg font-bold text-sm transition-all whitespace-nowrap cursor-pointer"
                style={{ background: "rgba(255,68,68,0.15)", color: "#ff4444", border: "1px solid rgba(255,68,68,0.3)" }}
              >
                Yes, Restart
              </button>
              <button
                onClick={() => setShowRestartConfirm(false)}
                className="flex-1 py-2.5 rounded-lg font-bold text-sm transition-all whitespace-nowrap cursor-pointer"
                style={{ border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.4)" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
